import { Injectable, Signal, inject, signal } from "@angular/core";
import { FlatEnrichmentModel } from "@app/shared/mappers/enrichment-detail.adapter";
import { EnrichmentPriceDirtyStore } from "./enrichment-price-dirty.store";

/** Which TAB currently holds unsaved edits, plus the discard snapshot.
 *  The two switch guards (POP + step) enforce a single dirty scope at a
 *  time, so ONE whole-model snapshot (taken at load, refreshed after every
 *  successful section save) is a safe discard target. Provided by the
 *  edit-package shell so it dies with the route. */
@Injectable()
export class SectionDirtyStore {
  private readonly priceDirty = inject(EnrichmentPriceDirtyStore);

  private readonly tab = signal<string | null>(null);
  readonly dirtyTab: Signal<string | null> = this.tab.asReadonly();

  private snap: FlatEnrichmentModel | null = null;

  /** Own monotonic edit counter — bumped on EVERY {@link markTabDirty} call
   *  (writers call it per form emission, so each call is a fresh edit
   *  signal even when the flag is already set). Never reset. */
  private edits = 0;

  /** Combined edit epoch across BOTH dirty scopes (tab flag + per-price
   *  set). The shell captures it when a section save starts; a different
   *  value on completion means the user kept editing while the PATCH was in
   *  flight — those edits were NOT in the sent body, so the success handler
   *  must not clear/snapshot them as saved. */
  editEpoch(): number {
    return this.edits + this.priceDirty.editEpoch();
  }

  /** The tab scope's own epoch, for the package save: moved when a package
   *  tab emitted while its PATCH was in flight. */
  tabEditEpoch(): number {
    return this.edits;
  }

  markTabDirty(tabId: string): void {
    this.edits++;
    if (this.tab() !== tabId) this.tab.set(tabId);
  }
  clearTab(): void {
    if (this.tab() !== null) this.tab.set(null);
  }

  /** True when anything on the page is unsaved (tab OR price scope). */
  isDirty(): boolean {
    return this.tab() !== null || this.priceDirty.dirtyIds().size > 0;
  }

  setSnapshot(model: FlatEnrichmentModel): void {
    this.snap = structuredClone(model);
  }
  /** A fresh clone per call so a restored model never shares refs. */
  snapshot(): FlatEnrichmentModel | null {
    return this.snap ? structuredClone(this.snap) : null;
  }
}
